import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Mail, Shield } from "lucide-react";

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen" data-testid="privacy-policy-page">
      <Header />
      
      <section className="pt-24 pb-16 bg-gradient-to-b from-primary/10 to-background">
        <div className="container text-center">
          <h1 className="text-4xl md:text-5xl font-serif text-primary mb-4 font-bold">Privacy Policy</h1>
          <p className="text-lg text-foreground/70 max-w-2xl mx-auto font-medium">
            Your privacy is important to us
          </p>
        </div>
      </section>

      <section className="py-12 bg-background">
        <div className="container max-w-4xl">
          <div className="prose prose-lg max-w-none">
            <div className="flex items-center gap-3 mb-4 p-4 bg-primary/5 rounded-lg">
              <Shield className="w-6 h-6 text-primary" />
              <p className="text-foreground/80 m-0">
                Siwon Galaxy / Siwon Harmony respects your privacy and is committed to protecting your personal information.
              </p>
            </div>
            <div className="mb-8 p-4 bg-primary/5 border-l-4 border-primary rounded-r-lg">
              <p className="text-sm text-foreground/80 m-0">
                <span className="font-semibold text-foreground">Operated by:</span> Siwon Health Care Resources Pvt. Ltd.<br />
                <span className="font-semibold text-foreground">Registered Address:</span> H.No. 330-B, Mianwali Colony, Gurgaon - 122001, Haryana, India<br />
                <span className="font-semibold text-foreground">CIN:</span> U85110HR2003PTC069710 &nbsp;·&nbsp; <span className="font-semibold text-foreground">GSTIN:</span> 06AAHCS1570M1Z3
              </p>
            </div>

            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-serif font-bold text-primary mb-3">1. Information We Collect</h2>
                <p className="text-foreground/80 leading-relaxed mb-3">
                  We may collect the following information:
                </p>
                <ul className="list-disc list-inside text-foreground/80 space-y-2 ml-4">
                  <li>Name and email address</li>
                  <li>Phone number</li>
                  <li>Billing details and payment status</li>
                  <li>Inquiry details and communication history</li>
                  <li>Quiz/assessment responses (for personalized coaching)</li>
                  <li>Usage data and preferences</li>
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-serif font-bold text-primary mb-3">2. How We Use Information</h2>
                <p className="text-foreground/80 leading-relaxed mb-3">
                  We use this information to:
                </p>
                <ul className="list-disc list-inside text-foreground/80 space-y-2 ml-4">
                  <li>Provide and deliver our coaching and consulting services</li>
                  <li>Process payments securely</li>
                  <li>Send service updates, newsletters, and promotional materials</li>
                  <li>Respond to your queries and support requests</li>
                  <li>Improve our offerings and user experience</li>
                  <li>Generate personalized coaching insights and recommendations</li>
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-serif font-bold text-primary mb-3">3. Payment Processing</h2>
                <p className="text-foreground/80 leading-relaxed">
                  Payments are processed by third-party payment gateways such as Razorpay. We do not store your card details, CVV, or banking credentials on our servers. All payment transactions are encrypted and handled securely by our payment partners who comply with PCI-DSS standards.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-serif font-bold text-primary mb-3">4. Data Sharing</h2>
                <p className="text-foreground/80 leading-relaxed mb-3">
                  We may share limited data only with:
                </p>
                <ul className="list-disc list-inside text-foreground/80 space-y-2 ml-4">
                  <li>Payment processors (Razorpay) for transaction processing</li>
                  <li>Hosting and cloud service providers</li>
                  <li>Analytics services to improve our offerings</li>
                  <li>Email delivery services for communications</li>
                  <li>Customer support tools</li>
                </ul>
                <p className="text-foreground/80 leading-relaxed mt-3">
                  We do not sell your personal information to third parties.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-serif font-bold text-primary mb-3">5. Data Security</h2>
                <p className="text-foreground/80 leading-relaxed">
                  We use reasonable security practices including encryption, secure servers, and access controls to protect your information. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-serif font-bold text-primary mb-3">6. Cookies</h2>
                <p className="text-foreground/80 leading-relaxed">
                  Our website may use cookies or similar technologies for functionality, analytics, and performance improvement. You can control cookie settings through your browser preferences.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-serif font-bold text-primary mb-3">7. Your Rights</h2>
                <p className="text-foreground/80 leading-relaxed mb-3">
                  You have the right to:
                </p>
                <ul className="list-disc list-inside text-foreground/80 space-y-2 ml-4">
                  <li>Request access to your personal data</li>
                  <li>Request correction of inaccurate data</li>
                  <li>Request deletion of your data (where legally applicable)</li>
                  <li>Opt-out of marketing communications</li>
                  <li>Withdraw consent for data processing</li>
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-serif font-bold text-primary mb-3">8. Data Retention</h2>
                <p className="text-foreground/80 leading-relaxed">
                  We retain your personal information for as long as necessary to fulfill the purposes outlined in this policy, unless a longer retention period is required by law.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-serif font-bold text-primary mb-3">9. Contact</h2>
                <p className="text-foreground/80 leading-relaxed mb-4">
                  For privacy-related queries, requests, or concerns, please contact us:
                </p>
                <div className="bg-muted/50 rounded-lg p-6">
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-primary" />
                    <a href="mailto:siwongalaxy108@gmail.com" className="text-primary hover:text-primary/80">
                      siwongalaxy108@gmail.com
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <p className="text-sm text-muted-foreground mt-12 pt-6 border-t border-border">
              Last updated: December 2025
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
